import {tokens} from '@commercial-helios/ui/theme'
import React, {useReducer} from 'react'
import styled from 'styled-components'

import Textarea from '@/components/textarea'
import {useIsCompanyEmail} from '@/hooks/use-is-company-email'
import useParams from '@/hooks/use-params'
import {getCompanySizeOptions} from '@/utils/get-company-size-options'
import {scrollToTop} from '@/utils/scroll'
import Checkbox from '@helios/shared/components/checkbox'
import {GlobalText} from '@helios/shared/components/global-text'
import Input from '@helios/shared/components/input'
import RichText from '@helios/shared/components/rich-text'
import Select from '@helios/shared/components/select'
import useFormErrors from '@helios/shared/hooks/use-form-errors'
import {useGetCountryName} from '@helios/shared/hooks/use-get-country-name'
import {useSubmitHubspotForm} from '@helios/shared/hooks/use-submit-hubspot-form'
import type {CountryCode} from '@helios/shared/locale/locales'
import locales from '@helios/shared/locale/locales'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {useGoogleReCaptcha} from '@helios/shared/providers/google-recaptcha'
import {useLocale} from '@helios/shared/providers/locale'
import {Text} from '@helios/shared/styles/typography'
import type CompanySize from '@helios/shared/types/company-size'
import type {GlobalStrings} from '@helios/shared/types/global-strings'
import type {HubspotContactProps} from '@helios/shared/types/hubspot-contact-props'
import {
    HubspotPartnerChannel,
    LeadType,
    LeadInterestLevel,
} from '@helios/shared/types/marketing-campaigns'
import {isGenericEmail} from '@helios/shared/utils/is-generic-email'

import * as s from '../lead-form.styles'
import * as tracking from '../tracking'

export const OTHER = 'Other' as const

export interface State {
    firstName: string
    lastName: string
    email: string
    country: CountryCode | typeof OTHER | ''
    partner_channel: HubspotPartnerChannel | undefined
    hasConsented: boolean
    partner: PartnerBigformPartnerId
    genericEmail: boolean
    companySize: CompanySize | ''
    message: string
}

export type PartnerBigformPartnerId = 'default' | 'datev'

enum ActionType {
    SET_VALUE,
}

type SetValue<TKey extends keyof State> = {
    type: ActionType.SET_VALUE
    key: keyof State
    value: State[TKey]
}

type Action<TKey extends keyof State> = SetValue<TKey>

const reducer = <TKey extends keyof State>(state: State, action: Action<TKey>) => {
    switch (action.type) {
        case ActionType.SET_VALUE: {
            return {
                ...state,
                [action.key]: action.value,
            }
        }

        default: {
            throw new Error('Unrecognized action')
        }
    }
}

const useValidateInputs = (globalStrings: GlobalStrings, partnerChannelError: string) => {
    return (state: State, isValidEmail: boolean) => {
        type Error = {
            key: keyof State
            value: string
        }

        const errors: Error[] = []

        if (!state.firstName.trim()) {
            errors.push({
                key: 'firstName',
                value: globalStrings.first_name_error,
            })
        }

        if (!state.lastName.trim()) {
            errors.push({
                key: 'lastName',
                value: globalStrings.last_name_error,
            })
        }

        if (!state.email.trim() || !isValidEmail) {
            errors.push({
                key: 'email',
                value: globalStrings.invalid_email_error,
            })
        }

        if (!state.partner_channel) {
            errors.push({
                key: 'partner_channel',
                value: partnerChannelError,
            })
        }

        if (!state.country.trim()) {
            errors.push({
                key: 'country',
                value: globalStrings.country_error,
            })
        }

        if (!state.hasConsented) {
            errors.push({
                key: 'hasConsented',
                value: globalStrings.has_consented_error,
            })
        }

        if (!state.companySize.trim()) {
            errors.push({
                key: 'companySize',
                value: globalStrings.company_size_error,
            })
        }

        return errors
    }
}

const isPartnerBigformPartnerId = (partner?: string): partner is PartnerBigformPartnerId => {
    return partner === 'default' || partner === 'datev'
}

export interface Props {
    formId: string
    hubspotProperties?: Partial<HubspotContactProps>
    content: {
        preTitle?: string
        title: string
        partner: string
        thankYouTitle: string
        thankYouSubtitle: string
        submitButtonText: string
        thankYouImageSrc: string
        showDescriptionField: boolean
        partnerChannel: {
            preselectedChannel?: HubspotPartnerChannel
            label: string
            accountingPartner: string
            affiliatePartner: string
            consultingPartner: string
            integrationPartner: string
            otherReseller: string
            error: string
            placeholder: string
        }
    }
}

export const PartnerBigform = ({formId, content, hubspotProperties}: Props) => {
    const currentLocale = useLocale()
    const getCountryName = useGetCountryName()
    const params = useParams()
    const globalStrings = useGlobalStrings()
    const validateInputs = useValidateInputs(globalStrings, content.partnerChannel.error)
    const [submissionError, setSubmissionError] = React.useState(false)
    const {isLoading, isSubmitted, recaptchaHubspotSubmit} = useSubmitHubspotForm(formId, false)
    const {loadReCaptcha} = useGoogleReCaptcha()

    React.useEffect(() => {
        tracking.bigformReached({
            country: currentLocale.country,
        })
    }, [currentLocale.country])

    const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        // Validate inputs
        const errors = validateInputs(state, isValidEmail)

        if (errors.length) {
            errors.forEach((error) => {
                setError(error.key, error.value)
            })

            return scrollToTop()
        }

        await recaptchaHubspotSubmit(
            {
                ...state,
                ...hubspotProperties,
                type_of_lead: isAccountingPartner ? LeadType.AccountingPartner : LeadType.Partner,
                genericEmail: isGenericEmail(state.email),
                lead_interest_level: !isAccountingPartner ? LeadInterestLevel.Interested : '',
            },
            undefined,
            () => setSubmissionError(true),
            () => {
                tracking.bigformSubmitted(state)

                if (isAccountingPartner) {
                    const params = new URLSearchParams({
                        firstName: state.firstName,
                        lastName: state.lastName,
                        email: state.email,
                        country: state.country,
                    })
                    window.location.assign(
                        `${process.env.GATSBY_PLEO_APP_URL}/partner/signup?${params}`
                    )
                }
            }
        )
    }

    const initialState: State = {
        firstName: '',
        lastName: '',
        email: params.get('email') || '',
        partner_channel: content.partnerChannel.preselectedChannel,
        country: currentLocale.country,
        hasConsented: false,
        partner: isPartnerBigformPartnerId(content.partner) ? content.partner : 'default',
        genericEmail: false,
        companySize: '',
        message: '',
    }

    const [state, dispatch] = useReducer(reducer, initialState)
    const isAccountingPartner = state.partner_channel === HubspotPartnerChannel.AccountingPartner

    const {errors, setError, clearError} = useFormErrors(state)
    const isValidEmail = useIsCompanyEmail(state.email || '')

    const setValue = <TKey extends keyof State>(key: TKey, value: State[TKey]) => {
        if (submissionError) setSubmissionError(false)

        dispatch({
            type: ActionType.SET_VALUE,
            key,
            value,
        })
        tracking.bigformInteracted(key)
    }

    const companySizeOptions = getCompanySizeOptions()
    const uniqueCountryCodes = Array.from(new Set(locales.map((locale) => locale.country)))

    const countryOptions = [
        ...uniqueCountryCodes.map((countryCode) => ({
            label: getCountryName(countryCode),
            value: countryCode,
        })),
        {
            label: globalStrings.other,
            value: OTHER,
        },
    ]

    const handleInputChange: typeof setValue = (key, value) => {
        loadReCaptcha()
        clearError(key)
        setValue(key, value)
    }

    const errorSpacePlaceholder = <Text variant="label-base">{'\u00a0'}</Text>

    if (isSubmitted && !isAccountingPartner) {
        return (
            <s.ThankYouContainer>
                <s.Titles>
                    <Text variant="title-base" color="muted" center>
                        {content.thankYouTitle}
                    </Text>

                    <Text variant="paragraph-base" center>
                        {content.thankYouSubtitle}
                    </Text>
                </s.Titles>
                <img src={content.thankYouImageSrc} alt="" />
            </s.ThankYouContainer>
        )
    }

    return (
        <s.Form onSubmit={handleFormSubmit} noValidate>
            <s.Titles>
                <Text variant="paragraph-base" color="muted" center>
                    {content.preTitle}
                </Text>

                <Text variant="title-base" center>
                    {content.title}
                </Text>
            </s.Titles>

            <s.InputsWrapper>
                <s.Row>
                    <div>
                        <Input
                            label={globalStrings.first_name}
                            value={state.firstName}
                            onChange={(value) => handleInputChange('firstName', value)}
                            error={errors.firstName}
                            required
                        />

                        {!errors.firstName && errorSpacePlaceholder}
                    </div>

                    <div>
                        <Input
                            label={globalStrings.last_name}
                            value={state.lastName}
                            onChange={(value) => handleInputChange('lastName', value)}
                            error={errors.lastName}
                            required
                        />

                        {!errors.lastName && errorSpacePlaceholder}
                    </div>
                </s.Row>

                <s.Row>
                    <div>
                        <Input
                            type="email"
                            label={globalStrings.yourEmailLabel}
                            value={state.email}
                            onChange={(value) => handleInputChange('email', value)}
                            error={errors.email}
                            required
                        />

                        {!errors.email && errorSpacePlaceholder}
                    </div>
                </s.Row>

                <s.Row>
                    <div>
                        <Select
                            label={content.partnerChannel.label}
                            options={[
                                {
                                    label: content.partnerChannel.accountingPartner,
                                    value: HubspotPartnerChannel.AccountingPartner,
                                },
                                {
                                    label: content.partnerChannel.affiliatePartner,
                                    value: HubspotPartnerChannel.AffiliatePartner,
                                },
                                {
                                    label: content.partnerChannel.consultingPartner,
                                    value: HubspotPartnerChannel.ConsultingPartner,
                                },
                                {
                                    label: content.partnerChannel.integrationPartner,
                                    value: HubspotPartnerChannel.TechnologyPartners,
                                },
                                {
                                    label: content.partnerChannel.otherReseller,
                                    value: HubspotPartnerChannel.OtherReseller,
                                },
                            ]}
                            placeholder={content.partnerChannel.placeholder}
                            value={state.partner_channel ?? ''}
                            onChange={(value) => handleInputChange('partner_channel', value)}
                            error={errors.partner_channel}
                            required
                        />

                        {!errors.partner_channel && errorSpacePlaceholder}
                    </div>
                </s.Row>

                <s.Row>
                    <div>
                        <Select
                            label={globalStrings.company_size}
                            options={companySizeOptions}
                            placeholder={globalStrings.select}
                            value={state.companySize}
                            onChange={(value) => handleInputChange('companySize', value)}
                            error={errors.companySize}
                            required
                            data-testid="hubspot-company-size-select"
                        />

                        {!errors.companySize && errorSpacePlaceholder}
                    </div>

                    <div>
                        <Select
                            label={globalStrings.country}
                            options={countryOptions}
                            placeholder={globalStrings.country_placeholder}
                            value={state.country}
                            onChange={(value) => handleInputChange('country', value)}
                            error={errors.country}
                            required
                        />

                        {!errors.country && errorSpacePlaceholder}
                    </div>
                </s.Row>

                {content.showDescriptionField && (
                    <s.Row>
                        <div>
                            <Textarea
                                label={globalStrings.interest_message_label}
                                value={state.message}
                                onChange={(value) => setValue('message', value)}
                            />
                        </div>
                    </s.Row>
                )}
            </s.InputsWrapper>

            <Checkbox
                label={globalStrings.consent_label}
                checked={state.hasConsented}
                onChange={(value) => handleInputChange('hasConsented', value)}
                error={errors.hasConsented}
            />

            <div>
                <s.SubmitButton loading={isLoading}>{content.submitButtonText}</s.SubmitButton>
                {submissionError && (
                    <s.ErrorText color="error" variant="label-base">
                        {globalStrings.form_submission_error}
                    </s.ErrorText>
                )}
                <StyledGlobalText
                    globalStringKey="financial_partner_disclaimer"
                    variant="label-base"
                />
            </div>

            <RichText
                variant="label-base"
                color="muted"
                content={globalStrings.recaptcha_message}
            />
        </s.Form>
    )
}

const StyledGlobalText = styled(GlobalText)`
    margin-top: ${tokens.spacing12};
`
